import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { DEFAULT_LOCALE, INTL_LOCALE, Locale } from '@/enums/locale'
import { Permission } from '@/enums/permission'
import i18n, { isLocale } from '@/libs/i18n'
import type { IMe } from '@/types/User'

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const stored       = localStorage.getItem('locale')
    const locale       = ref<Locale>(isLocale(stored) ? stored : DEFAULT_LOCALE)
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<IMe>()

    /*
     * Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() — never a
     * second source of truth. Two modes only, 'light' | 'dark' — no System option in the UI
     * (vue-spec theme.md). Without `emitAuto` the ref always reads the RESOLVED mode.
     */
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    // Keep the breakpoint LIVE: read once, `isMobile` would freeze the form factor (and
    // the mobile drawer) until a reload. Bound to the store's effect scope.
    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    /*
     * WHAT THE PERSON MAY DO — their permissions, the union of their roles' (GET /me). Every
     * answer is false until /me has come back. Any `dobavnice.*` implies `dobavnice.view`, any
     * `inventura.*` implies `inventura.view` and any `prenos.*` implies `prenos.view` and any
     * `urniki.*` implies `urniki.view`, as the
     * API's Logic enforces (docs/API.md → Modules and permissions). The API checks every call again; these only keep somebody from
     * being offered a button that would answer 403.
     */
    const can = (permission: Permission | string): boolean => {
        const held = userData.value?.permissions ?? []

        if (held.includes(permission)) return true
        if (permission === Permission.DobavniceView) return held.some((entry) => entry.startsWith('dobavnice.'))
        if (permission === Permission.InventuraView) return held.some((entry) => entry.startsWith('inventura.'))
        if (permission === Permission.PrenosView) return held.some((entry) => entry.startsWith('prenos.'))
        if (permission === Permission.UrnikiView) return held.some((entry) => entry.startsWith('urniki.'))

        return false
    }

    const canAny = (permissions: (Permission | string)[]): boolean => permissions.some(can)

    /*
     * A JWT cookie beside the header, so a scan in an <iframe> and an export behind an <a href>
     * are authenticated too — a browser cannot put an Authorization header on those.
     */
    const setJWTCookie = (jwt: string) => {
        const secure = window.location.protocol === 'https:' ? '; Secure' : ''

        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax${secure}`
        } else {
            document.cookie = `JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${secure}`
        }
    }

    // Restore the cookie on page load if the token is already in localStorage.
    setJWTCookie(JWT.value)

    const changeLocale = (newLocale: string) => {
        locale.value = isLocale(newLocale) ? newLocale : DEFAULT_LOCALE
        localStorage.setItem('locale', locale.value)
        ;(i18n.global.locale as unknown as { value: string }).value = locale.value
        document.documentElement.lang = INTL_LOCALE[locale.value]
    }

    /* The person's language wins once they are known: `Me.locale` is theirs wherever they sign in. */
    const setUserData = (data: IMe) => {
        userData.value = data

        if (isLocale(data.locale) && data.locale !== locale.value) changeLocale(data.locale)
    }

    const setLogin = (jwt: string, user?: IMe) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie(jwt)

        if (user) setUserData(user)
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value      = ''
        userData.value = undefined
        setJWTCookie('')
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    return {
        JWT, locale, userData, loaderActive, windowWidth, colorMode,
        getJWT, isLoggedIn, isLoaderActive, isMobile, can, canAny,
        setLogin, setLogout, setUserData, startLoading, stopLoading, changeLocale,
    }
})
